import type { BulkImport } from '@/types/bulkImport'

export interface BulkImportStatusChip {
  labelKey: string
  variant: 'success' | 'warning' | 'error' | 'info'
  isProgress: boolean
}

export function isPartialCompletion(failedCount: number, hasCardFailures: boolean): boolean {
  return failedCount > 0 || hasCardFailures
}

/**
 * Maps a bulk import's status to its canonical chip/progress descriptor.
 * Returns null for non-canonical/transient statuses (e.g. uploaded, validating, ready).
 * The consumer translates the labelKey and renders via EcxBadge or EcxProgressBar based on isProgress.
 */
export function getBulkImportStatusChip(
  input: Pick<BulkImport, 'status' | 'has_card_failures' | 'totals'>,
): BulkImportStatusChip | null {
  if (input.status === 'processing') {
    return {
      labelKey: 'corp.bulk_import.central.status_in_progress',
      variant: 'info',
      isProgress: true,
    }
  }

  if (input.status === 'completed') {
    if (isPartialCompletion(input.totals.failed, input.has_card_failures)) {
      return {
        labelKey: 'corp.bulk_import.central.status_partial',
        variant: 'warning',
        isProgress: false,
      }
    }
    return {
      labelKey: 'corp.bulk_import.central.status_completed',
      variant: 'success',
      isProgress: false,
    }
  }

  if (input.status === 'invalid') {
    return {
      labelKey: 'corp.bulk_import.central.status_invalid',
      variant: 'error',
      isProgress: false,
    }
  }

  if (input.status === 'failed') {
    return {
      labelKey: 'corp.bulk_import.central.status_failed',
      variant: 'error',
      isProgress: false,
    }
  }

  return null
}
